import { computed, Injectable, signal, WritableSignal } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Language } from '@nomoh-monorepo/shared-types';
import { formatarMoedaPorIdioma, simboloMoedaPorIdioma } from './moeda.util';

@Injectable({
  providedIn: 'root',
})
export class I18nService {
  private readonly STORAGE_KEY = 'app-language';
  private readonly MANUAL_KEY = 'app-language-manual';

  public idioma: WritableSignal<Language> = signal({
    code: 'pt',
    name: 'Português',
    flag: 'assets/flags/flag_bra.png',
  });

  public $currentLang = computed(() => this.idioma().code);

  public $locale = computed(() => {
    const code = this.idioma().code;
    switch (code) {
      case 'en':
        return 'en-US';
      case 'es':
        return 'es-ES';
      case 'fr':
        return 'fr-FR';
      default:
        return 'pt-BR';
    }
  });

  public $simboloMoeda = computed(() => simboloMoedaPorIdioma(this.idioma().code));

  formatarMoeda(valor: number | string | null | undefined): string {
    return formatarMoedaPorIdioma(valor, this.idioma().code, this.$locale());
  }

  public readonly supportedLanguages: Language[] = [
    { code: 'pt', name: 'Português', flag: 'assets/flags/flag_bra.png' },
    { code: 'en', name: 'English', flag: 'assets/flags/flag_us.png' },
    { code: 'es', name: 'Espanhol', flag: 'assets/flags/flag_esp.png' },
    { code: 'fr', name: 'Francês', flag: 'assets/flags/flag_france.png' },
  ];

  constructor(private translateService: TranslateService) {}

  /**
   * Define o idioma do sistema.
   * `escolhaManual` grava a preferência do seletor e passa a ter prioridade sobre o navegador.
   */
  setLanguage(code: string, escolhaManual = false): void {
    const lang = this.supportedLanguages.find((l) => l.code === code);
    if (lang) {
      this.idioma.set(lang);
      this.translateService.use(code);
      this.saveLanguageToStorage(code);
      if (escolhaManual) {
        this.marcarEscolhaManual();
      }
    }
  }

  /**
   * Idioma ativo do app.
   * Prioridade: escolha manual do seletor → primeiro idioma suportado do navegador → pt
   */
  getStoredLanguage(): string {
    const manual = this.lerEscolhaManual();
    if (manual) {
      return manual;
    }
    return this.idiomaDoNavegador() ?? 'pt';
  }

  private lerEscolhaManual(): string | null {
    try {
      if (localStorage.getItem(this.MANUAL_KEY) !== '1') {
        return null;
      }
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (stored && this.supportedLanguages.some((l) => l.code === stored)) {
        return stored;
      }
    } catch {
      // localStorage pode não estar disponível (SSR, navegador privado)
    }
    return null;
  }

  /** Percorre navigator.languages e fica com o primeiro código que o app traduz. */
  private idiomaDoNavegador(): string | undefined {
    const lista = this.idiomasDoNavegador();
    return lista.find((code) => this.supportedLanguages.some((l) => l.code === code));
  }

  private idiomasDoNavegador(): string[] {
    if (typeof navigator === 'undefined') {
      const unico = this.translateService.getBrowserLang();
      return unico ? [unico] : [];
    }
    const bruto = navigator.languages?.length
      ? Array.from(navigator.languages)
      : [navigator.language];
    return bruto
      .filter((item): item is string => !!item)
      .map((item) => item.split(/[-_]/)[0].toLowerCase());
  }

  /**
   * Salva idioma em localStorage
   * @param code Código do idioma
   */
  private saveLanguageToStorage(code: string): void {
    try {
      localStorage.setItem(this.STORAGE_KEY, code);
    } catch {
      // Silenciosamente falha se localStorage não disponível
    }
  }

  private marcarEscolhaManual(): void {
    try {
      localStorage.setItem(this.MANUAL_KEY, '1');
    } catch {
      // Silenciosamente falha se localStorage não disponível
    }
  }
}
